import { baseQuery, endpoints, formatErrors } from "utils/api/endpoints";
import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createApi } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { GlobalState } from "./types";

import {
	clearAppViewSession,
	readAppViewFromSession,
	writeAppViewToSession,
} from "utils/app-view-session";
import {
	getReduxStateData,
} from "utils/purchase-flow-storage";

// Check both localStorage and sessionStorage for user data
const getUserData = () => {
	const localData = localStorage.getItem("vle-user");
	const sessionData = sessionStorage.getItem("vle-user");
	return localData
		? JSON.parse(localData)
		: sessionData
		? JSON.parse(sessionData)
		: null;
};

const DEV_ACCESS_STORAGE_KEY = "vle_dev_access_verified";

const clearAllSessionAndLocalStorage = () => {
	try {
		const devAccessVerified = localStorage.getItem(DEV_ACCESS_STORAGE_KEY);

		sessionStorage.clear();
		localStorage.clear();

		if (devAccessVerified !== null) {
			localStorage.setItem(DEV_ACCESS_STORAGE_KEY, devAccessVerified);
		}
	} catch (error) {
		console.error("Error clearing storage on logout:", error);
	}
};

const getSavedLines = () => {
	const savedData = getReduxStateData();
	return savedData?.lines || 1;
};

export const initialState: GlobalState = {
	user: getUserData(),
	isAppView: readAppViewFromSession(),
	planData: null,
	checkAvailabilityDetails: null,
	lines: getSavedLines(),
	compatibilityData: null,
	compatibilitiesData: [],
	customerId: null,
	isPorting: false,
	portRequestSubmitted: false, // Initialize as false
	singleNPCheckSimData: null,
	multiNPCheckSimData: [],
	addLinesData: null,
	digitalSecurityCurrentAndUpgradePlans: null,
	singleNetworkDevice: null,
};

const slice = createSlice({
	name: "global",
	initialState,
	reducers: {
		setAppView(state, action: PayloadAction<string | null>) {
			state.isAppView = action.payload;
			writeAppViewToSession(action.payload);
		},
		setUser(
			state,
			action: PayloadAction<{ user: any; rememberDevice: boolean }>,
		) {
			console.log(
				"Setting user in global slice. RememberDevice:",
				action.payload.rememberDevice,
			);
			state.user = action.payload.user;

			// Clear both storages first
			localStorage.removeItem("vle-user");
			sessionStorage.removeItem("vle-user");

			// Store user data based on rememberDevice preference
			if (action.payload.user) {
				if (action.payload.rememberDevice) {
					console.log("Storing user in localStorage");
					localStorage.setItem("vle-user", JSON.stringify(action.payload.user));
				} else {
					console.log("Storing user in sessionStorage");
					sessionStorage.setItem(
						"vle-user",
						JSON.stringify(action.payload.user),
					);
				}
			} else {
				console.log("Clearing user data from storage");
				state.isAppView = null;
				clearAppViewSession();
				clearAllSessionAndLocalStorage();
			}
		},
		setPlanData(state, action: PayloadAction<any>) {
			state.planData = action.payload.planData;
			if (action.payload.month) {
				state.planData = { ...state.planData, month: action.payload.month };
			}
		},
		collectCheckAvailabilityDetails(state, action: PayloadAction<any>) {
			state.checkAvailabilityDetails = action.payload.CAD;
		},
		setLines(state, action: PayloadAction<any>) {
			state.lines = action.payload.numberOfLines;
		},
		setCompatibilityData(state, action: PayloadAction<any>) {
			state.compatibilityData = action.payload.CD;
		},
		setCompatibilitiesData(state, action: PayloadAction<any>) {
			state.compatibilitiesData = action.payload.CDS || [];
		},
		setIsPorting(state, action: PayloadAction<any>) {
			state.isPorting = action.payload;
		},
		setSingleNPCheckSimData(state, action: PayloadAction<any>) {
			state.singleNPCheckSimData = action.payload.checkSimData;
		},
		setMultiNPCheckSimData(state, action: PayloadAction<any>) {
			const combined = [
				...(state.multiNPCheckSimData || []),
				...(action.payload.checkSimData || []),
			];

			const seen = new Set();
			state.multiNPCheckSimData = combined.filter((item) => {
				const key = JSON.stringify(item);
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});
		},
		setAddLinesData(state, action: PayloadAction<any>) {
			state.addLinesData = action.payload;
		},
		setDigitalSecurityCurrentAndUpgradePlans(
			state,
			action: PayloadAction<any>,
		) {
			state.digitalSecurityCurrentAndUpgradePlans = action.payload;
		},
		setPortRequestSubmitted(state, action: PayloadAction<boolean>) {
			state.portRequestSubmitted = action.payload;
		},
		setSingleNetworkDevice(state, action: PayloadAction<any>) {
			state.singleNetworkDevice = action.payload;
		},
		updateUserPartial(state, action: PayloadAction<Partial<any>>) {
			if (!state.user) return;
			const updatedUser = { ...state.user, ...action.payload };

			// Update storage if it exists there
			const localData = localStorage.getItem("vle-user");
			const sessionData = sessionStorage.getItem("vle-user");

			state.user = updatedUser;

			if (localData) {
				localStorage.setItem("vle-user", JSON.stringify(updatedUser));
			} else if (sessionData) {
				sessionStorage.setItem("vle-user", JSON.stringify(updatedUser));
			}
		},
	},
});

export const api = createApi({
	reducerPath: "globalApi",
	baseQuery,
	// tagTypes: [],
	endpoints: (build) => ({
		providePlan: build.mutation<any, any>({
			query: (params) => {
				return {
					...endpoints.postPlan,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		checkAvailability: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.checkAvailability,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		sendOtpInCheckout: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.sendOtpInCheckout,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		verifyOtpInCheckout: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.verifyOtpInCheckout,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		resendOtpInCheckout: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.resendOtpInCheckout,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		verifyCustomer: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.verifyCustomer,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getDevices: build.query<any, any>({
			query: (params) => {
				return {
					url: endpoints.getDevices.url + "/" + params.networkType,
					method: endpoints.getDevices.method,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		checkDeviceCompatibility: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.deviceCompatibility,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		validatePorting: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.validatePort,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		checkSim: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.checkSim,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		orderSummary: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.orderSummary,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		checkout: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.checkout,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		subscriptionStatus: build.query<any, { subscriptionId: string }>({
			query: ({ subscriptionId }) => {
				return {
					...endpoints.subscriptionStatus,
					url: `${endpoints.subscriptionStatus.url}/${encodeURIComponent(
						subscriptionId,
					)}`,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getNewLine: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.getNewLine,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getTopupOrderSummary: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.topupOrderSummary,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getDigitalSecurityOrderSummary: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.digitalSecurityOrderSummary,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		createDigitalSecurityPayment: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.digitalSecurityCreatePayment,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		validateSim: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.validateSim,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		validateDevice: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.validateDevice,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getMobileSimInventoryBySimNumber: build.query<
			any,
			{ simNumber: string; imeiNumber?: string }
		>({
			query: ({ simNumber, imeiNumber }) => {
				const cleanSim = simNumber.replace(/\s/g, "");
				const cleanImei = String(imeiNumber || "").replace(/\s/g, "");
				return {
					url: endpoints.mobileSimInventoryBySimNumber.url,
					method: endpoints.mobileSimInventoryBySimNumber.method,
					params: {
						simNumber: cleanSim,
						...(cleanImei ? { imeiNumber: cleanImei } : {}),
					},
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getMobileSimInventoryPlans: build.query<any, void>({
			query: () => {
				return {
					...endpoints.mobileSimInventoryPlans,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getMobileSimInventorySecurityPlans: build.query<
			any,
			{ frequency: number; securityPlanName?: string }
		>({
			query: (params) => {
				return {
					...endpoints.mobileSimInventorySecurityPlans,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getDigitalSecurityUpgradeOrderSummary: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.digitalSecurityUpgradeOrderSummary,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getDigitalSecurityCurrentAndUpgradePlans: build.query<
			any,
			{ customerId: string }
		>({
			query: (params) => {
				return {
					...endpoints.digitalSecurityCurrentAndUpgradePlans,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getSecurityPlansOrderSummary: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.getSecurityPlansOrderSummary,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		upgradeSecurityPlansCheckout: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.upgradeSecurityPlansCheckout,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		digitalSecurityMobileUpgradeCheckout: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.digitalSecurityMobileUpgradeCheckout,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getPaymentMethods: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.getPaymentMethods,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getProfile: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.getProfile,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		getFreeProductVariant: build.query<any, void>({
			query: () => {
				return {
					...endpoints.freeProductVariant,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		renewPlanMakePaymentOrderSummary: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.renewPlanMakePaymentOrderSummary,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		renewPlanMakePaymentCheckout: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.renewPlanMakePaymentCheckout,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		subscriptionExtensionReactivationSummary: build.query<any, any>({
			query: (params) => {
				return {
					...endpoints.subscriptionExtensionReactivationSummary,
					params,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
		subscriptionExtensionReactivate: build.mutation<any, any>({
			query: (body) => {
				return {
					...endpoints.subscriptionExtensionReactivate,
					body,
				};
			},
			transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
		}),
	}),
});

export const { actions: globalActions } = slice;
export const {
	useGetDigitalSecurityOrderSummaryQuery,
	useGetDigitalSecurityUpgradeOrderSummaryQuery,
	useGetSecurityPlansOrderSummaryQuery,
	useDigitalSecurityMobileUpgradeCheckoutMutation,
	useSendOtpInCheckoutMutation,
	useVerifyOtpInCheckoutMutation,
	useResendOtpInCheckoutMutation,
} = api;

export const useGlobalSlice = () => {
	useInjectReducer({ key: slice.name, reducer: slice.reducer });
	useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
	return {
		actions: slice.actions,
		...api,
	};
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useGlobalSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
